import type { CSSProperties } from "react";
import { useLoaderData, useSearchParams } from "react-router-dom";
import {
  borderRadius,
  colors,
  fontSize,
  spacing,
  transitions,
} from "~/styles/tokens";
import { ROUTES } from "~/utils";
import { LandingPageLoaderType } from "./loaders";
import { ExploreFooterCta } from "./components/ExploreFooterCta";
import { ExploreHeader } from "./components/ExploreHeader";
import { ExploreHero } from "./components/ExploreHero";
import { ExploreHierarchySection } from "./components/ExploreHierarchySection";
import { ExploreLeaseFlowSection } from "./components/ExploreLeaseFlowSection";
// Kept for later — plans are intentionally hidden from Explore for now.
import { ExplorePricingSection } from "./components/ExplorePricingSection";
import { ExploreTenantBridge } from "./components/ExploreTenantBridge";
import { ExploreValueSection } from "./components/ExploreValueSection";
import "./components/explorePage.css";

/** Flip to true to show plan cards on Explore again. */
const SHOW_EXPLORE_PLANS = false;

function buildSignupUrl(returnUrl: string): string {
  if (!returnUrl) return "/signup";
  return `/signup?returnUrl=${encodeURIComponent(returnUrl)}`;
}

const exploreTokenStyle = {
  ["--explore-primary" as string]: colors.actionPrimary,
  ["--explore-success" as string]: colors.statusSuccess,
  ["--explore-warning" as string]: colors.statusWarning,
  ["--explore-secondary" as string]: colors.actionSecondary,
  ["--explore-tertiary" as string]: colors.actionTertiary,
  ["--explore-surface" as string]: colors.surfaceDefault,
  ["--explore-surface-subtle" as string]: colors.surfaceSubtle,
  ["--explore-surface-muted" as string]: colors.surfaceMuted,
  ["--explore-surface-property" as string]: colors.surfaceProperty,
  ["--explore-surface-building" as string]: colors.surfaceBuilding,
  ["--explore-surface-unit" as string]: colors.surfaceUnit,
  ["--explore-text" as string]: "#141414",
  ["--explore-text-secondary" as string]: colors.textSecondary,
  ["--explore-text-muted" as string]: colors.textMuted,
  ["--explore-text-inverse" as string]: colors.textInverse,
  ["--explore-border" as string]: colors.borderDefault,
  ["--explore-gap-inline" as string]: spacing.inline,
  ["--explore-gap-element" as string]: spacing.element,
  ["--explore-gap-component" as string]: spacing.component,
  ["--explore-gap-section" as string]: spacing.section,
  ["--explore-gap-page" as string]: spacing.page,
  ["--explore-gap-footer" as string]: spacing.footer,
  ["--explore-font-caption" as string]: fontSize.caption,
  ["--explore-font-body" as string]: fontSize.body,
  ["--explore-font-emphasis" as string]: fontSize.emphasis,
  ["--explore-font-subheading" as string]: fontSize.subheading,
  ["--explore-radius" as string]: borderRadius.card,
  ["--explore-motion-standard" as string]: transitions.standard,
  ["--explore-motion-quick" as string]: transitions.quick,
  ["--explore-lang-surface" as string]: "#003a8c",
} as CSSProperties;

/**
 * Explore page (`/explore`) — public marketing / signup entry.
 * Loader still uses the landing-page API for plans + affiliate ref capture.
 */
const LandingPage = () => {
  const { plans } = useLoaderData() as LandingPageLoaderType;
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get("returnUrl") || "";
  const signupUrl = buildSignupUrl(returnUrl);
  const browsePath = ROUTES.propertyTable;

  return (
    <div className="explore-page" style={exploreTokenStyle}>
      <ExploreHeader signupUrl={signupUrl} browsePath={browsePath} />
      <ExploreHero signupUrl={signupUrl} browsePath={browsePath} />
      <ExploreValueSection />
      <ExploreHierarchySection />
      <ExploreLeaseFlowSection />
      {SHOW_EXPLORE_PLANS ? (
        <ExplorePricingSection plans={plans} returnUrl={returnUrl} />
      ) : null}
      <ExploreTenantBridge />
      <ExploreFooterCta signupUrl={signupUrl} browsePath={browsePath} />
    </div>
  );
};

export default LandingPage;
